import { useState } from "react";

import { visibleTimelineRanges, type TrackingTimeline } from "../../tracking/player/timeline-status";
import type { TRenderableSegment } from "../../utils/tree";
import { mergeRanges, type TimeRange } from "../../utils/ranges";

type Range = TimeRange;
export type SegmentRetraction = Range & {
  fromStart: number;
  fromEnd: number;
  fromLeftRadius: number;
  fromRightRadius: number;
  startedAt: number;
  /** Retiring pending stripes, clipped to this fragment's initial painted bounds. */
  pending: readonly Range[];
};
export const RETRACTION_MS = 220;

export function segmentRetractionBounds(motion: SegmentRetraction, now: number) {
  const progress = Math.max(0, Math.min(1, (now - motion.startedAt) / RETRACTION_MS));
  const eased = 1 - (1 - progress) ** 3;
  return {
    start: motion.fromStart + (motion.start - motion.fromStart) * eased,
    end: motion.fromEnd + (motion.end - motion.fromEnd) * eased,
    leftRadius: motion.fromLeftRadius + (1 - motion.fromLeftRadius) * eased,
    rightRadius: motion.fromRightRadius + (1 - motion.fromRightRadius) * eased,
  };
}

function sameSource(current: TRenderableSegment, previous: TRenderableSegment) {
  if (
    current.type !== "point_box" ||
    previous.type !== "point_box" ||
    current.start !== previous.start ||
    (current.end ?? current.start) < (previous.end ?? previous.start) ||
    current.groupId !== previous.groupId
  )
    return false;
  return (
    current.coordinates.top_left.x === previous.coordinates.top_left.x &&
    current.coordinates.top_left.y === previous.coordinates.top_left.y &&
    current.coordinates.bottom_right.x === previous.coordinates.bottom_right.x &&
    current.coordinates.bottom_right.y === previous.coordinates.bottom_right.y
  );
}

function removedRanges(previous: Range, current: readonly Range[]): Range[] {
  const removed: Range[] = [];
  let start = previous.start;
  for (const range of current) {
    if (start < range.start) removed.push({ start, end: range.start });
    start = Math.min(previous.end, range.end);
  }
  if (start < previous.end) removed.push({ start, end: previous.end });
  return removed;
}

function firstRangeAfter(time: number, ranges: readonly Range[]) {
  let low = 0;
  let high = ranges.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (ranges[mid]!.end <= time) low = mid + 1;
    else high = mid;
  }
  return low;
}

/** Proof ranges are ordered status runs; reasons may split touching ranges. */
function coveredBy(range: Range, proof: readonly Range[]) {
  let end = range.start;
  for (let i = firstRangeAfter(range.start, proof); i < proof.length && end < range.end; i++) {
    if (proof[i]!.start > end) return false;
    end = Math.max(end, proof[i]!.end);
  }
  return end >= range.end;
}

/** Row-owned visual clocks; authoritative fragment geometry is never delayed. */
export function useSegmentRetractions(
  timeline: TrackingTimeline | null,
  rawSegments: readonly TRenderableSegment[],
): ReadonlyMap<string, SegmentRetraction> {
  const [snapshot, setSnapshot] = useState(() => ({
    timeline,
    rawSegments,
    motions: new Map<string, SegmentRetraction>(),
  }));
  if (snapshot.timeline === timeline && snapshot.rawSegments === rawSegments) return snapshot.motions;

  const motions = new Map<string, SegmentRetraction>();
  const now = performance.now();
  if (timeline && snapshot.timeline && timeline.revision === snapshot.timeline.revision) {
    const previousSources = new Map(snapshot.rawSegments.map((segment) => [segment.id, segment]));
    for (const source of rawSegments) {
      const previousSource = previousSources.get(source.id);
      const previous = snapshot.timeline.segments.get(source.id);
      const current = timeline.segments.get(source.id);
      if (
        !previousSource ||
        !sameSource(source, previousSource) ||
        !previous ||
        !current ||
        previous.start !== current.start ||
        current.end < previous.end
      )
        continue;

      const previousRanges = visibleTimelineRanges(previous);
      const currentRanges = visibleTimelineRanges(current);
      const pending = previous.runs.filter((run) => run.state === "pending");
      const currentPending = current.runs.filter((run) => run.state === "pending");
      const unavailable = current.runs.filter((run) => run.state === "unavailable");

      const children = previousRanges.map(() => [] as Range[]);
      const affected = new Set<number>();
      let oldIndex = 0;
      for (const range of currentRanges) {
        while (previousRanges[oldIndex] && previousRanges[oldIndex]!.end <= range.start) oldIndex++;
        const old = previousRanges[oldIndex];
        if (!old || old.start >= range.end) continue;
        const extendsTail =
          oldIndex === previousRanges.length - 1 &&
          old.end === previous.end &&
          current.end > previous.end &&
          range.end === current.end;
        if (range.start < old.start || (range.end > old.end && !extendsTail)) {
          // Merges/additions belong to the existing growth/reveal animation.
          for (let i = oldIndex; previousRanges[i] && previousRanges[i]!.start < range.end; i++) affected.add(i);
          continue;
        }
        children[oldIndex]!.push(range);
      }

      for (const [index, old] of previousRanges.entries()) {
        const surviving = children[index]!;
        if (affected.has(index) || !surviving.length) continue;
        const extendsTail = surviving.at(-1)!.end > old.end;
        // New GT may put the old ready tail back into processing. Only that
        // fragment loses its obsolete clock; an unrelated gap keeps moving.
        let renewed = false;
        for (
          let i = firstRangeAfter(old.start, currentPending);
          currentPending[i] && currentPending[i]!.start < old.end;
          i++
        ) {
          const range = {
            start: Math.max(old.start, currentPending[i]!.start),
            end: Math.min(old.end, currentPending[i]!.end),
          };
          if (!extendsTail && !coveredBy(range, pending)) {
            renewed = true;
            break;
          }
        }
        if (renewed) continue;
        const key = `${source.id}:${old.start}`;
        const candidate = snapshot.motions.get(key);
        const active = candidate && now - candidate.startedAt < RETRACTION_MS ? candidate : undefined;
        const removed = removedRanges(old, surviving);
        if (!removed.length) {
          if (active) {
            // The outer growth painter owns this new tail. Keep the inner gap
            // edge on its original clock while lifting only the outer cap.
            motions.set(key, extendsTail ? { ...active, end: surviving[0]!.end, fromEnd: surviving[0]!.end } : active);
          }
          continue;
        }
        if (!removed.every((range) => coveredBy(range, pending) && coveredBy(range, unavailable))) continue;

        const painted = active ? segmentRetractionBounds(active, now) : { ...old, leftRadius: 1, rightRadius: 1 };
        const retiring = mergeRanges([...removed, ...(active?.pending ?? [])]);
        for (const [childIndex, target] of surviving.entries()) {
          const left = surviving[childIndex - 1];
          const right = surviving[childIndex + 1];
          const fromStart = left ? (left.end + target.start) / 2 : painted.start;
          const fromEnd = right ? (target.end + right.start) / 2 : extendsTail ? target.end : painted.end;
          motions.set(`${source.id}:${target.start}`, {
            ...target,
            fromStart,
            fromEnd,
            fromLeftRadius: left ? 0 : painted.leftRadius,
            fromRightRadius: right ? 0 : painted.rightRadius,
            startedAt: now,
            pending: retiring.flatMap((range) => {
              const start = Math.max(fromStart, range.start);
              const end = Math.min(fromEnd, range.end);
              return start < end ? [{ start, end }] : [];
            }),
          });
        }
      }
    }
  }
  setSnapshot({ timeline, rawSegments, motions });
  return motions;
}
